
import React, { useState, useEffect } from 'react';
import { useClientLogoStore } from '@/lib/stores/clientLogoStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, ExternalLink, Plus, AlertCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLogoPreview } from '@/hooks/useLogoPreview';
import { ClientLogo } from '@/lib/types';
import { Spinner } from '@/components/ui/spinner';

const ClientLogosManager = () => {
  const { logos, loading, error, fetchLogos, addLogo, updateLogo, removeLogo } = useClientLogoStore();
  const { toast } = useToast();
  const { logoPreview, processLogo, clearLogo } = useLogoPreview();
  
  const [logoName, setLogoName] = useState('');
  const [logoAltText, setLogoAltText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch logos on component mount
  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 500000) { // 500KB max
      toast({
        title: "File too large",
        description: "Please select an image under 500KB",
        variant: "destructive"
      });
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }
    
    setImageFile(file);
    await processLogo(file);
  };
  
  const handleAddLogo = async () => {
    if (!logoName || !logoPreview) {
      toast({
        title: "Missing information",
        description: "Please provide a name and image for the logo",
        variant: "destructive"
      });
      return;
    }
    
    if (logos.length >= 20) {
      toast({
        title: "Maximum logos reached",
        description: "You can only showcase up to 20 logos. Please remove some before adding more.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await addLogo({
        name: logoName,
        imageUrl: logoPreview,
        altText: logoAltText || logoName,
        url: logoUrl || undefined
      });
      
      // Reset form
      setLogoName('');
      setLogoAltText('');
      setLogoUrl('');
      setImageFile(null);
      clearLogo();
      
      toast({
        title: "Logo added",
        description: "The logo has been added to the showcase"
      });
    } catch (err: any) {
      toast({
        title: "Error adding logo",
        description: err.message || "An error occurred while adding the logo",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRemoveLogo = async (id: string) => {
    try {
      await removeLogo(id);
      toast({
        title: "Logo removed",
        description: "The logo has been removed from the showcase"
      });
    } catch (err: any) {
      toast({
        title: "Error removing logo",
        description: err.message || "An error occurred while removing the logo",
        variant: "destructive"
      });
    }
  };
  
  const handleRefresh = () => {
    fetchLogos();
    toast({
      title: "Refreshed",
      description: "Logo list has been refreshed"
    });
  };
  
  if (error) {
    return (
      <div className="space-y-6">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Logos</h3>
          <p className="text-red-600">{error}</p>
          <Button 
            variant="outline" 
            className="mt-4" 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <Spinner className="mr-2 h-4 w-4" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Client Logos Showcase</h2>
        <div className="flex items-center gap-2">
          {logos.length >= 20 && (
            <div className="flex items-center text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              Maximum of 20 logos reached
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <Spinner className="mr-2 h-4 w-4" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-4">Add New Logo</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="logoName">Company Name *</Label>
                <Input
                  id="logoName"
                  value={logoName}
                  onChange={(e) => setLogoName(e.target.value)}
                  placeholder="e.g. Google"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="logoAltText">Alt Text</Label>
                <Input
                  id="logoAltText"
                  value={logoAltText}
                  onChange={(e) => setLogoAltText(e.target.value)}
                  placeholder="e.g. Google logo"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="logoUrl">Website URL (optional)</Label>
                <Input
                  id="logoUrl"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="e.g. https://google.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="logoImage">Logo Image *</Label>
                <Input
                  id="logoImage"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Max size: 500KB. Recommended: transparent PNG.</p>
              </div>
              
              <Button 
                onClick={handleAddLogo} 
                disabled={!logoName || !logoPreview || logos.length >= 20 || isSubmitting}
                className="bg-redflags-red hover:bg-redflags-red/90 text-white flex items-center gap-2"
              >
                {isSubmitting ? <Spinner className="h-4 w-4" /> : <Plus className="w-4 w-4" />}
                Add Logo
              </Button>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-4">Preview</h3>
            <div className="border rounded-lg h-40 flex items-center justify-center bg-gray-50">
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Logo preview" 
                  className="max-h-32 max-w-full object-contain"
                />
              ) : (
                <p className="text-sm text-muted-foreground">No logo selected</p>
              )}
            </div>
            {logoPreview && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setImageFile(null);
                  clearLogo();
                }}
                className="mt-2"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium mb-4">Manage Logos ({logos.length}/20)</h3>
        
        {loading && logos.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : logos.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No logos added yet. Add your first logo above.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Logo</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logos.map((logo) => (
                <TableRow key={logo.id}>
                  <TableCell>
                    <div className="h-12 w-24 flex items-center">
                      <img 
                        src={logo.imageUrl} 
                        alt={logo.altText || logo.name} 
                        className="max-h-10 max-w-full object-contain"
                      />
                    </div>
                  </TableCell>
                  <TableCell>{logo.name}</TableCell>
                  <TableCell>
                    {logo.url ? (
                      <a 
                        href={logo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-blue-600 hover:underline"
                      >
                        {new URL(logo.url).hostname}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(logo.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveLogo(logo.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ClientLogosManager;
